<template>
  <BaseEditModal
    :masterObj="master"
    :displayName="displayName"
    helpFileName="AssignChannel"
    :tabs="tabs"
    :saveFunc="save"
    :deleteFunc="archiveChannel"
    :instanceDeletable="!!currentChannel"
    :hideBottomButtons="''"
    :startTab="displayCueTakeTab ? 0 : 1"
    icon="mdi-layers"
    @close="$emit('close')"
    @activeTab="updateActiveTab">
    <template v-slot:append-buttons>
      <div
        v-if="activeTabIndex === 0"
        class="d-flex"
        style="width: 100%">
        <ClearChannelButton
          :disabled="!deluge.new_cued_dids?.[frameNo]"
          :channelUid="currentChannel?.uid" />
        <v-spacer />
        <TakeInChannelButton
          :disabled="!deluge.new_cued_dids?.[frameNo]"
          :channelUid="currentChannel?.uid" />
      </div>
    </template>
  </BaseEditModal>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { $http } from '../../stores/http.js';
import { deluge } from '@/stores/deluge.js';
import BaseEditModal from '../Shared/Modals/BaseEditModal.vue';
import AssignChannelCueAndTake from './AssignChannelModalComponents/AssignChannelCueAndTake.vue';
import AssignChannelAssign from './AssignChannelModalComponents/AssignChannelAssign.vue';
import AssignChannelCreate from './AssignChannelModalComponents/AssignChannelCreate.vue';

import TakeInChannelButton from './CueTake/TakeInChannelButton.vue';
import ClearChannelButton from './CueTake/ClearChannelButton.vue';

const props = defineProps({
  frameNo: {
    type: [String, Number],
    required: true,
  },
});
const emit = defineEmits(['close']);

const master = reactive({
  selectedChannelToAssign: undefined,
  newChannel: {
    name: '',
    description: '',
  },
});

const currentChannel = computed(() => {
  const frame = deluge.frames[props.frameNo];
  return frame ? deluge.channels[frame.did] : null;
});

const displayCueTakeTab = computed(() => {
  return !!(deluge.frames[props.frameNo] && deluge.frames[props.frameNo].did);
});

const activeTabIndex = ref(displayCueTakeTab.value ? 0 : 1);

onMounted(() => {
  if (currentChannel.value) {
    master.selectedChannelToAssign = currentChannel.value.did;
  }
});

const tabs = computed(() => [
  {
    id: 0,
    title: 'Cue & Take',
    icon: 'mdi-play-box-multiple-outline',
    component: AssignChannelCueAndTake,
    display: displayCueTakeTab.value,
    props: {
      frameNo: props.frameNo,
      currentChannel: currentChannel.value,
    },
    emits: {},
  },
  {
    id: 1,
    title: currentChannel.value ? 'Swap Channel' : 'Assign Channel',
    icon: currentChannel.value
      ? 'mdi-swap-horizontal'
      : 'mdi-arrow-right-bottom',
    component: AssignChannelAssign,
    display: true,
    props: {
      modelValue: master.selectedChannelToAssign,
      currentChannelDid: currentChannel.value?.did,
    },
    emits: {
      'update:modelValue': (did) => (master.selectedChannelToAssign = did),
      assign: () => swapChannel(),
      delete: () => archiveChannel(),
    },
  },
  {
    id: 2,
    title: 'Create Channel',
    icon: 'mdi-plus',
    component: AssignChannelCreate,
    display: true,
    props: {
      modelValue: master.newChannel,
    },
    emits: {
      'update:modelValue': (data) => Object.assign(master.newChannel, data),
      create: () => createChannelAndAssign(),
    },
  },
]);

const displayName = computed(() => {
  const tabTitles = {
    0: 'Cue & Take',
    1: currentChannel.value ? 'Swap Channel' : 'Assign Channel',
    2: 'Create & Assign Channel',
  };
  const title = tabTitles[activeTabIndex.value] || '';
  return `Frame ${props.frameNo}: ${title}`;
});

function updateActiveTab(newTabIndex) {
  activeTabIndex.value = newTabIndex;
}

async function save() {
  const tabId =
    tabs.value.find((t) => t.display)?.id === 0
      ? activeTabIndex.value
      : activeTabIndex.value + 1;

  if (tabId === 1) {
    // Assign/Swap
    await swapChannel();
  } else if (tabId === 2) {
    // Create
    await createChannelAndAssign();
  }
}

async function createChannelAndAssign() {
  const { name, description } = master.newChannel;
  if (!name) return;

  const baseUrl = `/dynamic_engine/v2/create_channel?name=${name}`;
  const frame_number = `&frame_number=${props.frameNo}`;
  const desc = description ? `&description=${description}` : '';

  await $http('POST', baseUrl + frame_number + desc);
  emit('close');
}

async function swapChannel() {
  if (
    !master.selectedChannelToAssign ||
    master.selectedChannelToAssign === currentChannel.value?.did
  ) {
    return;
  }
  await $http(
    'PUT',
    `/dynamic_engine/swap_channel?channel_did=${master.selectedChannelToAssign}&frame_id=${props.frameNo}`
  );
  emit('close');
}

async function archiveChannel() {
  await $http(
    'PUT',
    `/dynamic_engine/v2/remove_channel_in_frame?frame_number=${props.frameNo}`
  );
  emit('close');
}
</script>
